// src/components/Information.tsx
import React from 'react';
import { motion, Variants } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const sectionVariant: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: (delay: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: 'easeOut' },
  }),
};

const Information: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Helper funkce pro přidání EUR v závorkách (mimo český jazyk)
  const addEurIfNeeded = (text: string) => {
    if (i18n.language === 'cs') return text;

    // Najdi všechna čísla s "CZK" a přidej EUR v závorkách
    return text.replace(/(\d[\d\s,]*)\s*CZK/g, (_match: string, number: string) => {
      const cleanNumber = number.replace(/\s/g, '').replace(/,/g, '');
      const czk = parseInt(cleanNumber, 10);
      const eur = Math.round(czk / 25);
      return `${number} CZK (€${eur})`;
    });
  };

  return (
    <div className="min-h-screen bg-[#f7f2ed] text-[#3f2b25]">
      {/* Hero (bez pozadí) */}
      <section className="relative mb-12">
        <div className="relative max-w-5xl mx-auto px-6 py-20 flex flex-col items-center text-center">
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-4"
            initial="hidden"
            animate="visible"
            variants={sectionVariant}
            custom={0}
          >
            {t('info.title')}
          </motion.h1>
          <motion.p
            className="max-w-2xl text-lg font-medium"
            initial="hidden"
            animate="visible"
            variants={sectionVariant}
            custom={0.2}
          >
            {t('info.subtitle')}
          </motion.p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 space-y-12 pb-20">
        {/* Chata Laura */}
        <motion.div
          className="bg-white/90 backdrop-blur-md rounded-2xl p-10 shadow-lg ring-1 ring-[#4B2E2E]/10"
          initial="hidden"
          animate="visible"
          variants={sectionVariant}
          custom={0}
        >
          <h2 className="text-2xl font-semibold text-[#4B2E2E] mb-4">{t('info.section.house')}</h2>
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="flex-1 space-y-4">
              <p className="text-base leading-relaxed">
                {t('info.house.p1')}
              </p>
              <p className="text-base leading-relaxed">
                {t('info.house.p2')}
              </p>
            </div>
            <div className="flex-1 space-y-4">
              <p className="text-base leading-relaxed">
                {t('info.house.p3')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <div className="bg-[#f0ebe6] rounded-md p-4">
                  <p className="text-sm font-semibold">{t('info.house.capacity')}</p>
                  <p className="text-base">{t('info.house.capacityValue')}</p>
                </div>
                <div className="bg-[#f0ebe6] rounded-md p-4">
                  <p className="text-sm font-semibold">{t('info.house.amenities')}</p>
                  <p className="text-base">{t('info.house.amenitiesValue')}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Důležité informace pro hosty */}
        <motion.div
          className="bg-white/90 backdrop-blur-md rounded-2xl p-10 shadow-lg ring-1 ring-[#4B2E2E]/10"
          initial="hidden"
          animate="visible"
          variants={sectionVariant}
          custom={0.1}
        >
          <h2 className="text-2xl font-semibold text-[#4B2E2E] mb-4">
            {t('info.section.village')}
          </h2>
          <div className="space-y-4">
            <p className="text-base leading-relaxed">
              {addEurIfNeeded(t('info.village.p1'))}
            </p>
            <p className="text-base leading-relaxed">
              {t('info.village.p2')}
            </p>
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <span className="text-2xl">📹</span>
              <p className="text-base leading-relaxed font-medium text-amber-800">
                {t('info.village.p3')}
              </p>
            </div>
            <p className="text-base leading-relaxed">
              {addEurIfNeeded(t('info.village.p4'))}
            </p>
          </div>
        </motion.div>

        {/* Letní aktivity */}
        <motion.div
          className="bg-white/90 backdrop-blur-md rounded-2xl p-10 shadow-lg ring-1 ring-[#4B2E2E]/10"
          initial="hidden"
          animate="visible"
          variants={sectionVariant}
          custom={0.2}
        >
          <h2 className="text-2xl font-semibold text-[#4B2E2E] mb-4">{t('info.section.summer')}</h2>
          <p className="text-base leading-relaxed mb-4">
            {t('info.summer.p1')}
          </p>
          <p className="text-base leading-relaxed mb-6">
            {t('info.summer.p2')}
          </p>
          {/* Bazén a trampolína – od 26.6.2026 */}
          {(new Date() >= new Date('2026-06-26')) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              <div className="flex items-start gap-4 bg-blue-50 border border-blue-100 rounded-xl p-5">
                <span className="text-3xl">🏊</span>
                <div>
                  <p className="font-semibold text-[#4B2E2E] mb-1">{t('info.pool.title')}</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{t('info.pool.desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-4 bg-green-50 border border-green-100 rounded-xl p-5">
                <span className="text-3xl">🤸</span>
                <div>
                  <p className="font-semibold text-[#4B2E2E] mb-1">{t('info.trampoline.title')}</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{t('info.trampoline.desc')}</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Praktické informace */}
        <motion.div
          className="bg-white/90 backdrop-blur-md rounded-2xl p-10 shadow-lg ring-1 ring-[#4B2E2E]/10"
          initial="hidden"
          animate="visible"
          variants={sectionVariant}
          custom={0.3}
        >
          <h2 className="text-2xl font-semibold text-[#4B2E2E] mb-4">{t('info.section.practical')}</h2>
          <p className="text-base leading-relaxed">
            {t('info.practical.p1')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Information;
